/**
 * API клієнт для doc.wip_inventory — Iнвентаризація НЗВ.
 * Permission: production.wip_inventory.manage.
 *
 * Особливості:
 * - Status замість posted: 'draft' → 'posted' → 'cancelled'.
 * - /cancel замість /unpost (рішення 5-A — snapshot може лишатись у звітах).
 */
import { api } from './client';
import type { Page } from './catalogs';

export type WipInventoryStatus = 'draft' | 'in_progress' | 'posted' | 'cancelled';

export interface WipInventoryItemIn {
  line_number: number;
  nomenclature_id: string;
  quantity_factual: string;
  uom_id?: string | null;
  cost_per_unit?: string | null;
  total_cost?: string | null;
  comment?: string | null;
}

export interface WipInventoryItem extends WipInventoryItemIn {
  id: string;
}

export interface WipInventoryListItem {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  inventory_date: string;
  warehouse_id: string | null;
  status: WipInventoryStatus;
  deletion_mark: boolean;
}

export interface WipInventory extends WipInventoryListItem {
  department_id: string | null;
  posted_at: string | null;
  comment: string | null;
  project_id: string | null;
  items: WipInventoryItem[];
}

export interface WipInventoryCreate {
  organization_id: string;
  document_number?: string | null;
  document_date: string;
  inventory_date: string;
  warehouse_id?: string | null;
  department_id?: string | null;
  comment?: string | null;
  project_id?: string | null;
  items?: WipInventoryItemIn[];
}

export type WipInventoryUpdate = Partial<Omit<WipInventoryCreate, 'organization_id'>>;

export const wipInventory = {
  list: (params?: {
    organization_id?: string; date_from?: string; date_to?: string; sort_by?: string; sort_dir?: 'asc' | 'desc';
    status?: WipInventoryStatus;
    limit?: number;
    offset?: number;
  }) =>
    api.get<Page<WipInventoryListItem>>('/wip-inventory', { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<WipInventory>(`/wip-inventory/${id}`).then((r) => r.data),
  create: (payload: WipInventoryCreate) =>
    api.post<WipInventory>('/wip-inventory', payload).then((r) => r.data),
  update: (id: string, payload: WipInventoryUpdate) =>
    api.patch<WipInventory>(`/wip-inventory/${id}`, payload).then((r) => r.data),
  bookRemainders: (params: { organization_id: string; department_id?: string; as_of?: string }) =>
    api.get<{ items: { nomenclature_id: string; quantity: string; cost_per_unit: string | null; total_cost: string | null }[]; as_of: string }>(
      '/wip-inventory/book-remainders', { params }).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/wip-inventory/${id}`).then(() => undefined),
  post: (id: string) =>
    api.post<WipInventory>(`/wip-inventory/${id}/post`).then((r) => r.data),
  // /cancel замість /unpost — за специфікою wip_inventory lifecycle.
  cancel: (id: string) =>
    api.post<WipInventory>(`/wip-inventory/${id}/cancel`).then((r) => r.data),
};
